import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; // Import Link
import {
  UserGroupIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ChartBarIcon,
  AcademicCapIcon,
  DocumentArrowDownIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";

// 1. Added 'path' to each module to match your routes
const modules = [
  {
    icon: UserGroupIcon,
    title: "Employee Hub (HRIS)",
    desc: "Centralized employee records, job history, documents, personal info & department mapping.",
    gradient: "from-orange-400 to-red-500",
    path: "/hris" 
  },
  {
    icon: ClockIcon,
    title: "Attendance & Leave",
    desc: "GPS punch, selfie verification, shifts, holiday calendar, accrual rules & approval flows.",
    gradient: "from-teal-400 to-blue-500",
    path: "/attend"
  },
  {
    icon: CurrencyRupeeIcon,
    title: "Payroll & Compliance",
    desc: "Automated PF/ESI/PT/TDS, salary components, pay cycles, payslips & F&F settlement.",
    gradient: "from-indigo-400 to-purple-600",
    path: "/roll"
  },
  {
    icon: ChartBarIcon,
    title: "Performance & OKRs",
    desc: "Goal setting, OKRs, reviews, continuous feedback & appraisal cycles.",
    gradient: "from-pink-400 to-rose-500",
    path: "/okr"
  },
  {
    icon: AcademicCapIcon,
    title: "Training & LMS",
    desc: "Learning modules, courses, assessments, certifications & progress tracking.",
    gradient: "from-green-400 to-emerald-600",
    path: "/performance"
  },
  {
    icon: DocumentArrowDownIcon,
    title: "Document Vault",
    desc: "Secure storage for policies, letters, HR documents & employee-generated files.",
    gradient: "from-yellow-400 to-orange-500",
    path: "/doc"
  },
  {
    icon: WrenchScrewdriverIcon,
    title: "Helpdesk & Requests",
    desc: "HR queries, ticket categories, SLA tracking, escalations & resolution analytics.",
    gradient: "from-cyan-400 to-sky-600",
    path: "/desk"
  },
  {
    icon: BriefcaseIcon,
    title: "Recruitment Pipeline",
    desc: "Job postings, candidate tracking, interview rounds, evaluation & offer letters.",
    gradient: "from-violet-400 to-fuchsia-500",
    path: "/pipeline"
  },
];

export default function ModuleOverviewSection() {
  return (
    <section className="py-24 bg-white" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>
      <div className="max-w-7xl mx-auto px-6">
        
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center text-3xl md:text-4xl font-semibold text-gray-900"
        >
          Complete HR Suite
          <span className="block text-gray-800 font-medium">Powering Every Workflow</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center text-gray-600 max-w-2xl mx-auto mt-4 text-base leading-relaxed"
        >
          A unified HR platform designed to simplify daily operations across attendance, payroll, performance, helpdesk and more.
        </motion.p>

        {/* GRID */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {modules.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -6, scale: 1.02 }}
            >
              {/* Wrapped content in Link */}
              <Link to={m.path} className="block relative p-6 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all h-full">
                <div className={`absolute -top-4 left-6 w-12 h-12 rounded-xl shadow-md bg-gradient-to-r ${m.gradient} flex items-center justify-center`}>
                  <m.icon className="w-6 h-6 text-white" />
                </div>

                <h3 className="pt-10 text-lg font-semibold text-gray-900">{m.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed" style={{ fontFamily: "Roboto" }}>
                  {m.desc}
                </p>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}